import { useState } from 'react';
import { useCatalogDivisions } from '@/api/rpc/hooks/inventory-management/useItemCatalog';

export interface DivisionOption {
  id: string;
  title: string;
}

const NO_EMPTY_DIVISIONS: ReadonlySet<string> = new Set();

/**
 * The divisions that participate in the item catalog, in group/title order —
 * what every division control offers, whoever owns the selection. Narrower
 * than the org's full DataSource list on purpose: other feeds keep their own
 * per-division sources (the GAINS ingest repeats the same division titles),
 * and offering those would show every division twice, one of them matching
 * nothing. Shared so the catalog's Source filter (which keeps its selection in
 * the persisted query) and {@link useDivisionSelection} (which keeps its own)
 * read one list. The review's division counts read it too: deciding which
 * divisions are selectable takes the list first, and asking twice costs one
 * fetch.
 */
export function useDivisionOptions(): DivisionOption[] {
  const { data } = useCatalogDivisions();
  return data?.divisions ?? [];
}

/** The divisions that can be selected: every one with records here. */
function selectableDivisionIds(
  dataSources: readonly DivisionOption[],
  emptyIds: ReadonlySet<string>,
): string[] {
  return dataSources.flatMap((dataSource) =>
    emptyIds.has(dataSource.id) ? [] : [dataSource.id],
  );
}

/**
 * The divisions a view queries: the reader's pick (`chosenIds`; null = every
 * selectable division), less the ones with no records here (`emptyIds`).
 * Filtered against the divisions on offer rather than trusted: a stored
 * selection outlives the list it was made from — a division goes empty when
 * the worksheet changes, and one can leave the org's DataSources entirely.
 * Either way it is no longer a division, so it must not reach a query.
 */
export function effectiveDivisionSelection(
  dataSources: readonly DivisionOption[],
  emptyIds: ReadonlySet<string>,
  chosenIds: readonly string[] | null,
): string[] {
  const selectableIds = selectableDivisionIds(dataSources, emptyIds);
  const selectable = new Set(selectableIds);
  return (chosenIds ?? selectableIds).filter((id) => selectable.has(id));
}

/**
 * The catalog's divisions (see {@link useDivisionOptions}), plus the effective
 * division selection (defaults to all of them) — the flattened AS/400
 * worksheet reads from the selected divisions' linked ingest tables. Selection
 * is multi: the on-screen table combines the selected divisions and the export
 * bundles one file per division. Every division can be toggled off, in which
 * case the views show nothing until one is picked again. Shared by the Export
 * tab and its {@link DivisionSelector} so both keep the same list and default
 * without duplicating the derivation.
 *
 * `emptyIds` are divisions the current view has no records for: they are
 * unselectable, drop out of the selection, and are not what "all" means — the
 * review offers every division the org has, but a change set usually touches a
 * handful, and selecting the rest only ever produced an empty table. Pass none
 * (the default) and every division is selectable, which is what the list looks
 * like while the counts are still loading.
 */
export function useDivisionSelection(options?: {
  emptyIds?: ReadonlySet<string>;
  /** A selection restored from an earlier visit; null/absent = the default. */
  initialSelectedIds?: string[] | null;
}) {
  const emptyIds = options?.emptyIds ?? NO_EMPTY_DIVISIONS;
  const dataSources = useDivisionOptions();
  // null = "not yet chosen", so the effective selection can default to every
  // division derived during render (no effect syncing).
  const [selectedIds, setSelectedIds] = useState<string[] | null>(
    options?.initialSelectedIds ?? null,
  );
  const selectableIds = selectableDivisionIds(dataSources, emptyIds);
  const selectable = new Set(selectableIds);
  const selectedDataSourceIds = effectiveDivisionSelection(
    dataSources,
    emptyIds,
    selectedIds,
  );
  // By membership, not by count: an equal number of ids is not the same ids
  // once a stale one is in play (a division swapped for another leaves the
  // counts matching while the sets differ), and "all" would then read as on
  // with a division unselected — which turns the All button into a clear.
  const selected = new Set(selectedDataSourceIds);
  const allSelected =
    selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));

  // The given ids in division order — one pass over the divisions.
  const inDivisionOrder = (ids: ReadonlySet<string>): string[] => {
    const ordered: string[] = [];
    for (const d of dataSources) {
      if (ids.has(d.id)) ordered.push(d.id);
    }
    return ordered;
  };

  // Toggle a division in/out, keeping the list in division order.
  const toggleDataSource = (dataSourceId: string) => {
    if (!selectable.has(dataSourceId)) return;
    if (selectedDataSourceIds.includes(dataSourceId)) {
      setSelectedIds(selectedDataSourceIds.filter((id) => id !== dataSourceId));
      return;
    }
    setSelectedIds(
      inDivisionOrder(new Set([...selectedDataSourceIds, dataSourceId])),
    );
  };

  // The "All" control: select every division with records, or clear the
  // selection when they are already all on.
  const toggleAllDataSources = () =>
    setSelectedIds(allSelected ? [] : selectableIds);

  // Isolate one division: the selection becomes exactly it, whatever was on
  // before (a double-click on its button). Nothing to isolate to when it has
  // no records.
  const selectOnlyDataSource = (dataSourceId: string) => {
    if (!selectable.has(dataSourceId)) return;
    setSelectedIds([dataSourceId]);
  };

  // Bring divisions into the selection without taking any out — how rows just
  // authored for a division outside the view stay on screen once saved. Written
  // to the reader's own pick unfiltered: the division may still count as empty
  // until the counts refetch with its new row, and it joins the effective
  // selection the moment it doesn't. The default selection already means every
  // division with records, so it needs nothing.
  const includeDataSources = (dataSourceIds: readonly string[]) =>
    setSelectedIds((prev) => {
      if (prev === null) return prev;
      const next = new Set([...prev, ...dataSourceIds]);
      return next.size === prev.length ? prev : inDivisionOrder(next);
    });

  return {
    dataSources,
    selectedDataSourceIds,
    /** The reader's own pick, before defaulting — null until they make one. */
    chosenDataSourceIds: selectedIds,
    allSelected,
    toggleDataSource,
    toggleAllDataSources,
    selectOnlyDataSource,
    includeDataSources,
  };
}
